/**
 * Covers-and-details progress registry — survives SPA/bundle reloads.
 *
 * Keyed by library identity only (machine id). NEVER include album count
 * or web bundle SHA / Pages deployment id — a deploy must not invalidate progress.
 */

export type ArtworkAlbumPhaseState = 'filled' | 'exhausted' | 'pending';

/** Connector `thumb_cache` sentinel — art terminal, no grid JPEG. */
export const PLACEHOLDER_DONE_THUMB_HASH = '0000000000000001';

export const COVERS_DETAILS_REGISTRY_STORAGE_KEY = 'maestro:coversDetailsRegistry';

export type CoversDetailsRegistryBlob = {
  libraryKey: string;
  states: Record<string, ArtworkAlbumPhaseState>;
  /** Intentionally unused — documented so callers never start keying on it. */
  bundleVersion?: never;
  updatedAt: number;
};

const STATE_RANK: Record<ArtworkAlbumPhaseState, number> = {
  pending: 0,
  exhausted: 1,
  filled: 2,
};

function readStorage(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    return null;
  }
}

/** Last-known library key — reused when machineId is late (degraded IPC). */
export const COVERS_DETAILS_LAST_KEY_STORAGE = 'maestro:coversDetailsRegistry:lastKey';

const FALLBACK_LOCAL_KEY = 'machine:local';

/**
 * Stable library key — machine id only.
 * Do not include album count (grid_entry 2685 vs get_albums 2693 drifts)
 * or web bundle SHA (deploys must not invalidate).
 * Empty machineId returns '' — never invent `machine:local` (that missed
 * the persisted blob on every IPC-degraded boot).
 */
export function coversDetailsLibraryKey(opts: {
  machineId?: string | null;
  /** Accepted for call-site compat; ignored so count drift cannot miss the registry. */
  albumCount?: number;
}): string {
  const machine = String(opts.machineId || '').trim();
  if (!machine) return '';
  return `machine:${machine}`;
}

function readStoredBlob(): CoversDetailsRegistryBlob | null {
  const storage = readStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(COVERS_DETAILS_REGISTRY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CoversDetailsRegistryBlob;
    if (!parsed || typeof parsed.states !== 'object' || !parsed.libraryKey) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Persisted library key from the last successful hydrate/save. */
export function peekStoredCoversDetailsLibraryKey(): string | null {
  const storage = readStorage();
  if (!storage) return null;
  try {
    const last = String(storage.getItem(COVERS_DETAILS_LAST_KEY_STORAGE) || '').trim();
    if (last.startsWith('machine:')) return last;
  } catch { /* ignore */ }
  const blob = readStoredBlob();
  const key = String(blob?.libraryKey || '').trim();
  return key.startsWith('machine:') ? key : null;
}

function rememberLibraryKey(libraryKey: string): void {
  const storage = readStorage();
  if (!storage || !libraryKey.startsWith('machine:')) return;
  try {
    storage.setItem(COVERS_DETAILS_LAST_KEY_STORAGE, libraryKey);
  } catch { /* quota / private mode */ }
}

/**
 * Resolve the registry key without inventing a fresh one.
 * Live machineId wins; otherwise reuse last-known. Null = defer start.
 */
export function resolveCoversDetailsLibraryKey(machineId?: string | null): string | null {
  const live = coversDetailsLibraryKey({ machineId });
  const stored = peekStoredCoversDetailsLibraryKey();
  if (live) {
    if (stored && stored !== live && stored === FALLBACK_LOCAL_KEY) {
      rekeyCoversDetailsRegistry(live);
    }
    rememberLibraryKey(live);
    return live;
  }
  if (stored) return stored;
  return null;
}

/** Keep settled states when upgrading `machine:local` → `machine:<id>`. */
export function rekeyCoversDetailsRegistry(nextLibraryKey: string): void {
  if (!nextLibraryKey) return;
  const blob = readStoredBlob();
  if (!blob) {
    rememberLibraryKey(nextLibraryKey);
    return;
  }
  if (blob.libraryKey === nextLibraryKey) {
    rememberLibraryKey(nextLibraryKey);
    return;
  }
  saveCoversDetailsRegistry(nextLibraryKey, blob.states);
}

export function loadCoversDetailsRegistry(libraryKey: string): Record<string, ArtworkAlbumPhaseState> {
  if (!libraryKey) return {};
  const blob = readStoredBlob();
  if (!blob) return {};
  if (blob.libraryKey === libraryKey) return { ...blob.states };
  // Late machineId: stored under machine:local (Jul 26 fallback) — reuse, don't miss.
  if (blob.libraryKey === FALLBACK_LOCAL_KEY && libraryKey.startsWith('machine:')) {
    return { ...blob.states };
  }
  // Degraded boot: requested key empty/local, stored key is the real machine.
  if (libraryKey === FALLBACK_LOCAL_KEY && blob.libraryKey.startsWith('machine:')) {
    return { ...blob.states };
  }
  return {};
}

export function saveCoversDetailsRegistry(
  libraryKey: string,
  states: Map<string, ArtworkAlbumPhaseState> | Record<string, ArtworkAlbumPhaseState>,
): void {
  const storage = readStorage();
  if (!storage || !libraryKey) return;
  const record: Record<string, ArtworkAlbumPhaseState> = states instanceof Map
    ? Object.fromEntries(states.entries())
    : { ...states };
  const blob: CoversDetailsRegistryBlob = {
    libraryKey,
    states: record,
    updatedAt: Date.now(),
  };
  try {
    storage.setItem(COVERS_DETAILS_REGISTRY_STORAGE_KEY, JSON.stringify(blob));
    rememberLibraryKey(libraryKey);
  } catch {
    /* quota / private mode — best-effort */
  }
}

/** Prefer the more terminal of live bootstrap vs persisted (filled > exhausted > pending). */
export function mergeArtworkState(
  live: ArtworkAlbumPhaseState,
  persisted?: ArtworkAlbumPhaseState | null,
): ArtworkAlbumPhaseState {
  if (!persisted) return live;
  return STATE_RANK[persisted] > STATE_RANK[live] ? persisted : live;
}

export function isPlaceholderDoneThumbHash(
  hash: string | null | undefined,
): boolean {
  const key = String(hash || '').trim().toLowerCase();
  return key === PLACEHOLDER_DONE_THUMB_HASH;
}

/** 16-char grid thumb key served at GET /thumb/{key}. */
export function isGridThumbHash(hash: string | null | undefined): boolean {
  const key = String(hash || '').trim().toLowerCase();
  return /^[0-9a-f]{16}$/.test(key) && !isPlaceholderDoneThumbHash(key);
}

export type AlbumArtReconcileInput = {
  album_key?: string | null;
  id?: string | null;
  title?: string | null;
  artist_name?: string | null;
  year?: number | string | null;
  genre?: string | null;
  track_count?: number | null;
  cover_art_thumb_hash?: string | null;
  coverArtThumbHash?: string | null;
  embedded_cover_hash?: string | null;
  folder_cover_hash?: string | null;
  external_cover_hash?: string | null;
  cover_art_url?: string | null;
  coverUrl?: string | null;
  enrichment_attempted_at?: string | null;
  placeholder?: boolean | null;
};

function readThumbHash(album: AlbumArtReconcileInput): string {
  return String(album.cover_art_thumb_hash || album.coverArtThumbHash || '').trim();
}

/** Title + artist present — year/genre/track_count optional but strengthen "details". */
export function hasPopulatedAlbumDetails(album: AlbumArtReconcileInput | null | undefined): boolean {
  if (!album) return false;
  const title = String(album.title || '').trim();
  const artist = String(album.artist_name || '').trim();
  if (!title || !artist) return false;
  const year = album.year;
  const genre = String(album.genre || '').trim();
  const tracks = Number(album.track_count ?? 0);
  return Boolean(
    (typeof year === 'number' && year > 0)
    || (typeof year === 'string' && year.trim().length >= 4)
    || genre.length > 0
    || tracks > 0,
  );
}

/** Art is terminal in catalog: real grid thumb, placeholder_done, or legacy local/URL art. */
export function isAlbumArtTerminal(album: AlbumArtReconcileInput | null | undefined): boolean {
  if (!album) return false;
  const thumb = readThumbHash(album);
  if (isPlaceholderDoneThumbHash(thumb)) return true;
  if (isGridThumbHash(thumb)) return true;
  if (album.placeholder === true && hasPopulatedAlbumDetails(album)) return true;
  const emb = String(album.embedded_cover_hash || '').trim();
  const fol = String(album.folder_cover_hash || '').trim();
  const ext = String(album.external_cover_hash || '').trim();
  if (/^[0-9a-f]{64}$/i.test(emb) || /^[0-9a-f]{64}$/i.test(fol) || /^[0-9a-f]{64}$/i.test(ext)) {
    return true;
  }
  const url = String(album.cover_art_url || album.coverUrl || '').trim();
  if (!url) return false;
  if (url.includes('/front-500')) return false;
  if (url.includes('coverartarchive.org/release-group/')) return false;
  return true;
}

/**
 * Hydrate registry phase from live catalog row — prefer terminal catalog truth
 * over stale persisted `pending`.
 */
export function reconcileArtworkBootstrapState(
  album: AlbumArtReconcileInput,
  opts?: { enrichmentRetryOk?: boolean },
): ArtworkAlbumPhaseState {
  const retryOk = opts?.enrichmentRetryOk !== false;
  if (isAlbumArtTerminal(album)) {
    return isPlaceholderDoneThumbHash(readThumbHash(album)) ? 'exhausted' : 'filled';
  }
  if (
    retryOk
    && album.enrichment_attempted_at
    && !shouldRetryEnrichmentTimestamp(album.enrichment_attempted_at)
  ) {
    return 'exhausted';
  }
  if (hasPopulatedAlbumDetails(album)) {
    // Populated metadata but no art source yet — still eligible for enrichment.
    return 'pending';
  }
  return 'pending';
}

/** Mirror enrichmentService.shouldRetryEnrichment without importing enrichment layer. */
export function shouldRetryEnrichmentTimestamp(
  enrichmentAttemptedAt: string | null | undefined,
  retryMs = 7 * 24 * 60 * 60 * 1000,
): boolean {
  if (!enrichmentAttemptedAt) return true;
  const t = Date.parse(enrichmentAttemptedAt);
  if (!Number.isFinite(t)) return true;
  return Date.now() - t >= retryMs;
}

export function isTerminalArtworkPhase(state?: ArtworkAlbumPhaseState | null): boolean {
  return state === 'filled' || state === 'exhausted';
}

export function clearCoversDetailsRegistryForTests(): void {
  const storage = readStorage();
  if (!storage) return;
  try {
    storage.removeItem(COVERS_DETAILS_REGISTRY_STORAGE_KEY);
    storage.removeItem(COVERS_DETAILS_LAST_KEY_STORAGE);
  } catch { /* ignore */ }
}
